import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { platforms } from '../config/platforms';
import { useCrosspost, PlatformStatus } from '../hooks/useCrosspost';
import { openIntent } from '../utils/openIntent';
import { supabase } from '../lib/supabase';
import { PlatformIcon } from './PlatformIcon';
import './CrosspostModal.css';

interface CrosspostModalProps {
  isOpen: boolean;
  content: string;
  userId: string;
  onClose: (result?: { allSuccessful: boolean }) => void;
  attachments?: Array<{ type: 'image'; url: string; alt?: string }>;
  replyToPostId?: string;
  quotePostId?: string;
  previewMode?: 'default' | 'failed' | null;
}

type PlatformKey = keyof typeof platforms;

type PublishedPostLinks = {
  id: string;
  twitter: { url: string } | null;
  bluesky: { url: string } | null;
  farcaster: { url: string } | null;
};

const defaultPreviewStatuses: Record<string, PlatformStatus> = {
  twitter: { status: 'success' },
  bluesky: { status: 'pending' },
  farcaster: { status: 'error', error: 'Example error for preview', code: 'UNKNOWN' },
};

const failedPreviewStatuses: Record<string, PlatformStatus> = {
  twitter: {
    status: 'error',
    error: '$.media is missing but it is required.',
    code: 'INVALID_REQUEST',
  },
  bluesky: { status: 'success' },
  farcaster: {
    status: 'error',
    error: 'Publishing failed because the signer is no longer authorized.',
    code: 'TOKEN_EXPIRED',
  },
};

export const CrosspostModal: React.FC<CrosspostModalProps> = ({ isOpen, content, userId, onClose, attachments = [], replyToPostId, quotePostId, previewMode = null }) => {
  const navigate = useNavigate();
  const { statuses: liveStatuses, posting: livePosting, publishedPostId, handleRetry, startCrossposting, abort, enabledPlatforms: liveEnabledPlatforms, postingMethods: livePostingMethods } = useCrosspost();
  const isPreview = previewMode !== null;
  const previewStatuses = previewMode === 'failed' ? failedPreviewStatuses : defaultPreviewStatuses;

  // In preview mode, show all platforms with mock data; otherwise use live data
  const statuses = isPreview ? previewStatuses : liveStatuses;
  const posting = isPreview ? false : livePosting;
  const enabledPlatforms = isPreview
    ? Object.fromEntries(Object.keys(platforms).map(k => [k, true])) as Record<PlatformKey, boolean>
    : liveEnabledPlatforms;
  const postingMethods = isPreview
    ? Object.fromEntries(Object.keys(platforms).map(k => [k, 'oauth'])) as Record<PlatformKey, string>
    : livePostingMethods;

  const [now, setNow] = useState(Date.now());
  const retriedRef = useRef<Set<PlatformKey>>(new Set());
  const hasPostedRef = useRef(false);
  const [publishedPost, setPublishedPost] = useState<PublishedPostLinks | null>(null);
  const [isLoadingPost, setIsLoadingPost] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      retriedRef.current.clear();
      return;
    }

    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isPreview) {
      return;
    }

    const rateLimitedKeys = (Object.keys(statuses) as PlatformKey[]).filter((key) => {
      const status = statuses[key];
      return enabledPlatforms[key] && status?.status === 'error' && status.code === 'RATE_LIMIT' && typeof status.resetAt === 'number';
    });

    retriedRef.current.forEach((key) => {
      if (!rateLimitedKeys.includes(key)) {
        retriedRef.current.delete(key);
      }
    });

    rateLimitedKeys.forEach((key) => {
      const status = statuses[key];
      if (!status?.resetAt || status.resetAt > now || retriedRef.current.has(key)) {
        return;
      }
      retriedRef.current.add(key);
      handleRetry(key);
    });
  }, [enabledPlatforms, handleRetry, isOpen, isPreview, now, statuses]);

  const handleReconnect = () => {
    abort();
    onClose({ allSuccessful: false });
    navigate('/account');
  };

  const handleOpenIntent = (key: PlatformKey) => {
    const url = platforms[key].intentUrl + encodeURIComponent(content);
    openIntent(url);
  };

  const formatRemaining = (resetAt: number) => {
    const remaining = Math.max(0, Math.floor((resetAt - now) / 1000));
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const renderStatusCopy = (key: PlatformKey, status: PlatformStatus): JSX.Element => {
    switch (status.status) {
      case 'already-posted':
        return (
          <span className="status-icon-wrapper warning">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="status-icon warning-icon">
              <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            Already posted
          </span>
        );
      case 'error': {
        if (status.code === 'UNSUPPORTED_MEDIA') {
          return (
            <span className="status-icon-wrapper warning">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="status-icon warning-icon">
                <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {status.error || 'X/Twitter could not post one of the selected images.'}
            </span>
          );
        }
        if (status.code === 'RATE_LIMIT' && status.resetAt) {
          return (
            <span className="status-icon-wrapper warning">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="status-icon warning-icon">
                <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              Rate-limited {formatRemaining(status.resetAt)}
            </span>
          );
        }
        if (status.code === 'TOKEN_EXPIRED') {
          return (
            <span className="status-icon-wrapper error">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="status-icon error-icon">
                <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {isPreview ? 'Session expired' : <>Session expired <button onClick={handleReconnect} className="intent-link">Reconnect</button></>}
            </span>
          );
        }
        return (
          <span className="status-icon-wrapper error">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="status-icon error-icon">
              <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            {isPreview ? 'Failed' : <button onClick={() => handleOpenIntent(key)} className="intent-link">Open Intent</button>}
          </span>
        );
      }
      default:
        return getStatusContent(key); // reuse existing for pending/success/manual etc.
    }
  };

  useEffect(() => {
    if (isPreview) return; // Skip actual posting in preview mode
    if (isOpen) {
      if (!hasPostedRef.current) {
        startCrossposting(content, userId, attachments, replyToPostId, quotePostId);
        hasPostedRef.current = true;
      }
    } else {
      hasPostedRef.current = false; // Reset on close
    }
  }, [isOpen, content, userId, attachments, replyToPostId, quotePostId, startCrossposting, isPreview]);

  const handleClose = () => {
    abort();
    onClose({ allSuccessful: isFullySuccessful });
  };

  const activePlatforms = (Object.keys(platforms) as PlatformKey[]).filter(key => enabledPlatforms[key]);
  const allSuccessful = activePlatforms.length > 0 && activePlatforms.every(key => statuses[key]?.status === 'success');
  const isFullySuccessful = !posting && allSuccessful;

  useEffect(() => {
    if (!isOpen) {
      setPublishedPost(null);
      setIsLoadingPost(false);
      return;
    }

    if (!isFullySuccessful || !publishedPostId) {
      setPublishedPost(null);
      setIsLoadingPost(false);
      return;
    }

    let isMounted = true;
    const fetchPost = async () => {
      setIsLoadingPost(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          if (isMounted) setPublishedPost(null);
          return;
        }
        const base = process.env.REACT_APP_API_URL;
        const isLocalHost = /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+)$/.test(window.location.hostname);
        const url = isLocalHost
          ? `/api/posts/${publishedPostId}`
          : (base ? `${base.replace(/\/$/, '')}/api/posts/${publishedPostId}` : `/api/posts/${publishedPostId}`);
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        if (!response.ok) {
          if (isMounted) setPublishedPost(null);
          return;
        }
        const data = await response.json();
        if (isMounted) {
          setPublishedPost({
            id: data.id,
            twitter: data.twitter?.url ? { url: data.twitter.url } : null,
            bluesky: data.bluesky?.url ? { url: data.bluesky.url } : null,
            farcaster: data.farcaster?.url ? { url: data.farcaster.url } : null,
          });
        }
      } catch (error) {
        if (isMounted) setPublishedPost(null);
      } finally {
        if (isMounted) setIsLoadingPost(false);
      }
    };

    fetchPost();
    return () => {
      isMounted = false;
    };
  }, [isOpen, isFullySuccessful, publishedPostId]);

  console.log('CrosspostModal render - isOpen:', isOpen, 'content:', content, 'userId:', userId);
  if (!isOpen) return null;

  const errorEntries = (Object.keys(statuses) as PlatformKey[])
    .filter(key => enabledPlatforms[key] && statuses[key]?.status === 'error' && statuses[key]?.error)
    .map(key => ({
      key,
      platformName: platforms[key].name,
      message: statuses[key].error as string,
    }));

  const getStatusContent = (key: PlatformKey): JSX.Element => {
    const postUrl = publishedPost 
      ? (key === 'twitter' ? publishedPost.twitter?.url 
        : key === 'bluesky' ? publishedPost.bluesky?.url 
        : key === 'farcaster' ? publishedPost.farcaster?.url 
        : null)
      : null;

    switch (statuses[key]?.status) {
      case 'pending':
        return (
          <span className="status-icon pending">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-loader"><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line></svg>
          </span>
        );
      case 'success':
        return (
          <span className="status-icon-wrapper success">
            <span className="status-icon success">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-check-circle"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            </span>
            {postUrl && !isLoadingPost && (
              <a 
                href={postUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="status-post-link"
                title="View post"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-arrow-up-right">
                  <line x1="7" y1="17" x2="17" y2="7"></line>
                  <polyline points="7 7 17 7 17 17"></polyline>
                </svg>
              </a>
            )}
          </span>
        );
      case 'manual':
        return (
          <span className="status-icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="status-icon">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
              <polyline points="10 17 15 12 10 7"></polyline>
              <line x1="15" y1="12" x2="3" y2="12"></line>
            </svg>
          </span>
        );
      case 'error':
        return (
          <span className="status-icon-wrapper error">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="status-icon error-icon">
              <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </span>
        );
      case 'popup-blocked':
        return (
          <span className="status-icon-wrapper warning">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="status-icon warning-icon">
              <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            Popup Blocked <button onClick={() => handleRetry(key)} className="intent-link">Retry</button>
          </span>
        );
      default:
        return <></>;
    }
  };

  const getPostingMethodLabel = (key: PlatformKey) => (
    postingMethods[key] === 'oauth' ? 'OAuth' : 'Intent'
  );

  return (
    <div className="crosspost-modal-overlay">
      <div className="crosspost-modal">
        <div className="crosspost-modal-header">
          <h4 className="crosspost-modal-title">Crossposting...</h4>
        </div>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {(Object.keys(platforms) as PlatformKey[]).filter(key => enabledPlatforms[key]).map(key => (
            <li key={key} className="crosspost-status-row">
              <div className="crosspost-platform-identity">
                <PlatformIcon platformKey={key} icon={platforms[key].icon} size={24} className="crosspost-platform-icon" />
                <span className="crosspost-platform-name">{platforms[key].name}</span>
                {platforms[key].maxChars && content.length > (platforms[key].maxChars as number) &&
                  <div className="length-warning" title={`Post exceeds ${platforms[key].name}'s character limit by ${content.length - (platforms[key].maxChars as number)} characters. It may be unsuccessful if you don't have a premium account.`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-alert-triangle"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                    <span style={{ fontSize: '11px', marginLeft: '4px' }}>+{content.length - (platforms[key].maxChars as number)}</span>
                  </div>
                }
              </div>
              <div className="crosspost-status-details">
                <span className="crosspost-method-label">
                  {getPostingMethodLabel(key)}
                </span>
                {renderStatusCopy(key, statuses[key] as PlatformStatus)}
              </div>
            </li>
          ))}
        </ul>
        {errorEntries.length > 0 && (
          <div className="crosspost-error-details">
            {errorEntries.map(({ key, platformName, message }) => (
              <p key={key} className="crosspost-error-detail-item">
                <strong>{platformName}:</strong> {message}
              </p>
            ))}
          </div>
        )}
        <div className="crosspost-modal-footer">
          <button onClick={handleClose} className="crosspost-secondary-button">
            {posting ? 'Cancel' : 'Finish'}
          </button>
        </div>
      </div>
    </div>
  );
};
